import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Shield, Download } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import api from '../lib/api';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function fmt(n) {
  if (!n || n === 0) return '-';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
}

function ReportRow({ row }) {
  return (
    <TableRow>
      <TableCell className="font-mono text-sm">{row.employee_code}</TableCell>
      <TableCell className="font-medium">{row.employee_name}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{row.organization}</TableCell>
      <TableCell className="text-right font-mono">{fmt(row.gross_salary)}</TableCell>
      <TableCell className="text-right font-mono">{fmt(row.basic)}</TableCell>
      <TableCell className="text-right font-mono text-orange-600">{fmt(row.epf_employee)}</TableCell>
      <TableCell className="text-right font-mono text-blue-600">{fmt(row.epf_employer)}</TableCell>
      <TableCell className="text-right font-mono font-semibold">{fmt(row.total_pf)}</TableCell>
    </TableRow>
  );
}

function ReportRows({ records }) {
  const elements = [];
  for (let i = 0; i < records.length; i++) {
    elements.push(<ReportRow key={i} row={records[i]} />);
  }
  return <>{elements}</>;
}

export default function EPFReportPage() {
  const now = new Date();
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [orgFilter, setOrgFilter] = useState('__all__');
  const [reportData, setReportData] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/organizations').then(res => setOrganizations(res.data)).catch(() => {});
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      let url = '/epf-report?month=' + month + '&year=' + year;
      if (orgFilter && orgFilter !== '__all__') url += '&organization_id=' + orgFilter;
      const res = await api.get(url);
      setReportData(res.data);
    } catch (e) {
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, [month, year, orgFilter]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const totals = reportData ? reportData.totals : null;

  return (
    <DashboardLayout>
      <div data-testid="epf-report-page" className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">EPF Report</h1>
          <p className="text-muted-foreground">Monthly Employee & Employer PF breakdown for government filing</p>
        </div>

        {/* Filters */}
        <div className="flex items-end gap-4 flex-wrap">
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Month</label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">January</SelectItem>
                <SelectItem value="2">February</SelectItem>
                <SelectItem value="3">March</SelectItem>
                <SelectItem value="4">April</SelectItem>
                <SelectItem value="5">May</SelectItem>
                <SelectItem value="6">June</SelectItem>
                <SelectItem value="7">July</SelectItem>
                <SelectItem value="8">August</SelectItem>
                <SelectItem value="9">September</SelectItem>
                <SelectItem value="10">October</SelectItem>
                <SelectItem value="11">November</SelectItem>
                <SelectItem value="12">December</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Year</label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2026">2026</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">Organization</label>
            <Select value={orgFilter} onValueChange={setOrgFilter}>
              <SelectTrigger className="w-56"><SelectValue placeholder="All Organizations" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Organizations</SelectItem>
                {organizations.filter(o => o.epf_enabled).map(o => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary Cards */}
        {totals && totals.employee_count > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xl font-bold font-mono">{totals.employee_count}</p>
                <p className="text-xs text-muted-foreground">Employees</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xl font-bold font-mono">{fmt(totals.total_basic)}</p>
                <p className="text-xs text-muted-foreground">Total Basic</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xl font-bold font-mono text-orange-600">{fmt(totals.total_employee_pf)}</p>
                <p className="text-xs text-muted-foreground">Employee PF (12%)</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xl font-bold font-mono text-blue-600">{fmt(totals.total_employer_pf)}</p>
                <p className="text-xs text-muted-foreground">Employer PF (12%)</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xl font-bold font-mono text-emerald-600">{fmt(totals.total_pf)}</p>
                <p className="text-xs text-muted-foreground">Total PF (Govt)</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Table */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                {MONTHS[parseInt(month) - 1]} {year} — PF Breakdown
              </CardTitle>
              {totals && totals.employee_count > 0 && (
                <Badge variant="outline" className="text-xs">{totals.employee_count} employees</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Loading...</div>
            ) : !reportData || reportData.records.length === 0 ? (
              <div className="p-12 text-center">
                <Shield className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground">No EPF records for this month.</p>
                <p className="text-xs text-muted-foreground mt-1">EPF is calculated when salary records are created for EPF-enabled organizations.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Emp ID</TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead className="text-right">Gross</TableHead>
                      <TableHead className="text-right">Basic</TableHead>
                      <TableHead className="text-right">Employee PF</TableHead>
                      <TableHead className="text-right">Employer PF</TableHead>
                      <TableHead className="text-right">Total PF</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <ReportRows records={reportData.records} />
                    {/* Totals row */}
                    <TableRow className="bg-muted/50 font-semibold">
                      <TableCell colSpan={4} className="text-right">Totals:</TableCell>
                      <TableCell className="text-right font-mono">{fmt(totals.total_basic)}</TableCell>
                      <TableCell className="text-right font-mono text-orange-600">{fmt(totals.total_employee_pf)}</TableCell>
                      <TableCell className="text-right font-mono text-blue-600">{fmt(totals.total_employer_pf)}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600 font-bold">{fmt(totals.total_pf)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
